/**
 * Application route path constants
 *
 * Centralized location for all application routes to prevent
 * typos and make route refactoring easier.
 */

/**
 * URL query param the Smokeball Requirements panel link, the "Add
 * representative" task CTA and the Contacts tab's own mount check all share
 * (VALE-1320) — the deep link that opens `ContactDetailsDrawer`'s add mode
 * with the LPR role pre-selected. Same "?param on the destination URL"
 * convention as `PMS_SYNC_OPEN_PARAM` (`pms-sync-force-show-context.tsx`).
 */
export const CONTACTS_ADD_LPR_PARAM = 'addContact';
export const CONTACTS_ADD_LPR_VALUE = 'lpr';

/**
 * The merged Estate account's matter-relative subroute, the one definition both
 * `MATTER_ROUTES.ESTATE_ACCOUNT` and the sidebar's `distributionSectionLanding`
 * build from. It lived at `distribution/statement` until VALE-1933 named the
 * address after the surface; `next.config.ts` redirects the old path.
 */
export const ESTATE_ACCOUNT_SUBROUTE = 'distribution/estate-account';

/** Matter detail routes - uses /matters/:id/:tab format */
export const MATTER_ROUTES = {
  /** Full-page create-matter flow */
  NEW: '/matters/new',
  DETAIL: (matterId: number) => `/matters/${matterId}`,
  OVERVIEW: (matterId: number) => `/matters/${matterId}/overview`,
  DOCUMENTS: (matterId: number) => `/matters/${matterId}/documents`,
  CORRESPONDENCE: (matterId: number) => `/matters/${matterId}/correspondence`,
  CONTACTS: (matterId: number) => `/matters/${matterId}/contacts`,
  INVENTORY: (matterId: number) => `/matters/${matterId}/inventory`,
  INSTITUTIONS: (matterId: number) => `/matters/${matterId}/institutions`,
  TIMELINE: (matterId: number) => `/matters/${matterId}/timeline`,
  DISCOVERY: (matterId: number) => `/matters/${matterId}/discovery`,
  REDEMPTION: (matterId: number) => `/matters/${matterId}/redemption`,
  DISTRIBUTION: (matterId: number) => `/matters/${matterId}/distribution`,
  ENTITLEMENTS: (matterId: number) => `/matters/${matterId}/entitlements`,
  BENEFICIARIES: (matterId: number) =>
    `/matters/${matterId}/distribution/beneficiaries`,
  ESTATE_ACCOUNT: (matterId: number) =>
    `/matters/${matterId}/${ESTATE_ACCOUNT_SUBROUTE}`,
  /** Reviewing one uploaded trust statement against the Estate account. */
  TRUST_STATEMENT_REVIEW: (matterId: number, statementId: number) =>
    `/matters/${matterId}/${ESTATE_ACCOUNT_SUBROUTE}/trust-statements/${statementId}`,
  DISTRIBUTION_PLANNER: (matterId: number) =>
    `/matters/${matterId}/distribution/planner`,
  DISTRIBUTION_PLANNER_EDIT: (matterId: number, distributionId: number) =>
    `/matters/${matterId}/distribution/planner?distributionId=${distributionId}`,
  RECORDINGS: (matterId: number) => `/matters/${matterId}/recordings`,
  INSTITUTION_DETAIL: (matterId: number, institutionId: number) =>
    `/matters/${matterId}/institutions?institution=${institutionId}`
} as const;

/**
 * The **retired** `?lead=<id>` deep-link param (LF-6015 → LF-6099).
 *
 * Nothing writes it any more: an enquiry has one address, `DETAIL`. It survives
 * only so the board (`leads-board-view.tsx`) can recognise an old pasted link
 * or bookmark and send it to the enquiry's page. Do not reach for it in new code.
 */
export const LEAD_DEEP_LINK_PARAM = 'lead';

/**
 * Enquiry routes (`functional-spec.md` §3 / §7).
 *
 * **One URL per enquiry, one presentation.** `DETAIL` is the enquiry's page,
 * and every entry point opens it: a board row, a pasted link, an email, a
 * refresh (VALE-2061). The board used to open an enquiry as a drawer over
 * itself; the board now remembers its tab, filters and scroll instead, so a
 * return from the page lands where the coordinator left it
 * (`lead-board-return-state.ts`).
 *
 * This replaced a `BOARD_DRAWER` (`/enquiries?lead=<id>`) form, which was two
 * URLs for one entity — and the worse of the two to share, since the board's
 * tab and filters are component state, so it landed a colleague on the default
 * board rather than on the enquiry.
 */
export const LEAD_ROUTES = {
  BOARD: '/enquiries',
  DETAIL: (leadId: number) => `/enquiries/${leadId}`
} as const;

/** Account routes */
export const ACCOUNT_ROUTES = {
  ROOT: '/account',
  PROFILE: '/account/profile',
  FIRM: '/account/firm',
  BRANDING: '/account/branding',
  TEAM: '/account/team',
  INTEGRATIONS: '/account/integrations',
  INTAKE: '/account/intake-questions',
  BILLING: '/account/billing'
} as const;

/** Integration routes */
export const INTEGRATION_ROUTES = {
  LIST: '/account/integrations',
  SMOKEBALL: {
    CONNECT: '/integrations/smokeball/connect',
    CALLBACK: '/integrations/smokeball/callback',
    CONFIGURE: '/integrations/smokeball/configure'
  },
  ACTIONSTEP: {
    CALLBACK: '/integrations/actionstep/callback',
    CONFIGURE: '/integrations/actionstep/configure'
  }
} as const;

/** Safe Custody routes */
export const SAFE_CUSTODY_ROUTES = {
  LIST: '/safe-custody',
  DETAIL: (packetId: number) => `/safe-custody?packet=${packetId}`
} as const;
